import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import type { UserDisplaySettings } from '@/models/user';
import { Copy, ExternalLink } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';
import { logFirebaseEvent } from '@/lib/tracking';

type ActiveWorkspace = UserDisplaySettings['activeWorkspace'];

interface MarketingToolsCardProps {
  activeWorkspace: ActiveWorkspace;
}

export const MarketingToolsCard: React.FC<MarketingToolsCardProps> = ({ activeWorkspace }) => {
  const slug = activeWorkspace.slug || '';
  const openLinkUrl = slug ? `${window.location.origin}/open/${encodeURIComponent(slug)}` : '';

  const widgetScriptTag = useMemo(() => {
    if (!slug) return '';
    return `<script src="${window.location.origin}/widget.js" data-lendlifted-slug="${slug}" data-lendlifted-label="Financing Available" data-lendlifted-cta="See payment options"></script>`;
  }, [slug]);

  const inlineScriptTag = useMemo(() => {
    if (!slug) return '';
    return `<script src="${window.location.origin}/widget.js" data-lendlifted-slug="${slug}" data-lendlifted-style="inline" data-lendlifted-label="Pay over time available"></script>`;
  }, [slug]);

  const modalScriptTag = useMemo(() => {
    if (!slug) return '';
    return `<script src="${window.location.origin}/widget.js" data-lendlifted-slug="${slug}" data-lendlifted-open="modal" data-lendlifted-label="Financing Available" data-lendlifted-cta="Check options without leaving this page"></script>`;
  }, [slug]);

  const copy = async (value: string, eventName: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      sonnerToast.success(message);
      logFirebaseEvent(eventName, {
        workspace_slug: slug,
      });
    } catch {
      sonnerToast.error('Could not copy automatically.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Marketing Tools</CardTitle>
            <CardDescription className="text-slate-600">
              Add a financing badge to your website and route visitors straight to your open payment link.
            </CardDescription>
          </div>
          <Badge variant="secondary" className="rounded-full">Widget MVP</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!slug ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-700">
            Set up your Open Payment Link slug first to generate a widget snippet.
          </div>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-sm font-medium text-slate-900">Open Payment Link</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="rounded bg-white px-2 py-1 text-xs text-slate-700">{openLinkUrl}</code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-200 bg-white hover:bg-slate-50"
                  onClick={() => copy(openLinkUrl, 'widget_open_link_copied', 'Open link copied.')}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
                <Button asChild type="button" variant="outline" size="sm" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50">
                  <a href={openLinkUrl} target="_blank" rel="noreferrer noopener">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open
                  </a>
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">Floating website badge (copy/paste)</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-200 bg-white hover:bg-slate-50"
                  onClick={() => copy(widgetScriptTag, 'widget_script_copied', 'Widget script copied.')}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Script
                </Button>
              </div>
              <Textarea readOnly value={widgetScriptTag} rows={3} className="rounded-xl border-slate-200 bg-white font-mono text-xs focus-visible:ring-slate-300" />
              <p className="text-xs text-muted-foreground">
                Paste before the closing <code>{'</body>'}</code> tag on your site. Opens your LendLifted open payment link in a new tab.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">Modal widget (beta)</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-200 bg-white hover:bg-slate-50"
                  onClick={() => copy(modalScriptTag, 'widget_script_copied', 'Modal widget script copied.')}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Modal
                </Button>
              </div>
              <Textarea readOnly value={modalScriptTag} rows={3} className="rounded-xl border-slate-200 bg-white font-mono text-xs focus-visible:ring-slate-300" />
              <p className="text-xs text-muted-foreground">
                Opens the LendLifted open-payment flow in an iframe modal on your site. Falls back to hosted checkout flow inside the modal.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-900">Inline badge (optional)</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-slate-200 bg-white hover:bg-slate-50"
                  onClick={() => copy(inlineScriptTag, 'widget_script_copied', 'Inline widget script copied.')}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Inline
                </Button>
              </div>
              <Textarea readOnly value={inlineScriptTag} rows={3} className="rounded-xl border-slate-200 bg-white font-mono text-xs focus-visible:ring-slate-300" />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
